import React, { useState, useRef, useEffect } from "react";
import {
  Select,
  MenuItem,
  Box,
  FormControl,
  Typography,
  TextField,
  ListItemText,
  InputAdornment,
  OutlinedInput,
} from "@mui/material";
import { ReactComponent as CheckedIcon } from "../../../assets/Checkbox.svg";
import { ReactComponent as UnCheckedIcon } from "../../../assets/unCheckbox.svg";
import SearchIcon from "@mui/icons-material/Search";
import CommonColorUtils from "../../../utils/common_colors";
import StringUtils from "../../../utils/string_utils";

const styles = {
  formControl: {
    width: "120px",
    mr: 1,
    mt: 1.5,
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      alignItems: "flex-start",
      paddingTop: "2px",
      paddingBottom: "2px",
      minHeight: "48px",
    },
  },
  select: {
    "& .MuiSelect-select": {
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      padding: "4px 12px",
      gap: "2px",
    },
  },
  labelText: {
    fontSize: 12,
    color: CommonColorUtils.primaryGrey600,
    lineHeight: 1.2,
  },
  valueText: {
    fontSize: 14,
    fontWeight: 600,
    color: "#000",
    lineHeight: 1.4,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "block",
    maxWidth: "100%"
  },
  menuPaper: {
    maxHeight: 400,
    overflowY: "auto",
    paddingTop: 0,
    boxShadow: "none",
    border: `1px solid ${CommonColorUtils.kborderColorPortal}`,
    borderRadius: "8px",
  },
  searchContainer: {
    px: 1,
    py: 0.5,
    position: "sticky",
    top: 0,
    backgroundColor: "white",
    zIndex: 1,
  },
  searchField: {
    "& .MuiInputBase-input": { fontSize: 14, py: 0.5 },
    "& .MuiOutlinedInput-root": { height: 34 },
  },
  menuItem: {
    padding: "6px",
    margin: "6px 8px",
    borderRadius: "4px",
    minHeight: 34,
    display: "flex",
    alignItems: "center",
    "&:hover": { backgroundColor: CommonColorUtils.khoverColor },
  },
  iconBox: {
    display: "flex",
    alignItems: "center",
    mr: 1,
    svg: { width: 16, height: 16 },
  },
  optionText: { fontSize: 14, fontWeight: 500 },
  noResults: { color: CommonColorUtils.noResultsTextColor, fontSize: 14 },
};

const TaskTypeMultiSelectDropdown = ({
  options = [],
  selectedValues,
  onChange,
  disabled
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);

  const filteredOptions = options.filter((item) =>
    item.label.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSelectAllToggle = () => {
    if (selectedValues.length === options.length) onChange([]);
    else onChange(options.map((opt) => opt.value));
  };

  const handleOptionToggle = (optionValue) => {
    if (selectedValues.includes(optionValue)) {
      onChange(selectedValues.filter((v) => v !== optionValue));
    } else {
      onChange([...selectedValues, optionValue]);
    }
  };

  useEffect(() => {
    if (open && inputRef.current)
      setTimeout(() => inputRef.current.focus(), 100);
  }, [open]);

  const selectedLabels =
  selectedValues.length === options.length
    ? "All"
    : options
        .filter((opt) => selectedValues.includes(opt.value))
        .map((opt) => opt.label)
        .join(", ");


  return (
    <FormControl fullWidth variant="outlined" sx={styles.formControl}>
      <Select
        data-testid="task-type-select"
        multiple
        disabled={disabled}
        displayEmpty
        open={open}
        onOpen={() => !disabled && setOpen(true)}
        onClose={() => {
          setOpen(false);
          setSearchQuery("");
        }}
        value={selectedValues}
        input={<OutlinedInput />}
        renderValue={() => (
          <Box>
            <Typography sx={styles.labelText}>
              {StringUtils.taskType}
            </Typography>
            <Typography sx={{...styles.valueText}}>
              {selectedLabels || StringUtils.defaultSelectLabel}
            </Typography>
          </Box>
        )}
        MenuProps={{
          PaperProps: { style: styles.menuPaper },
          autoFocus: false,
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "center",
          },
          transformOrigin: {
            vertical: "top",
            horizontal: "center",
          },
        }}
        sx={styles.select}
      >
        <Box
          sx={styles.searchContainer}
          onKeyDown={(e) => e.stopPropagation()}
          data-testid="task-type-search-container" 
        >
          <TextField
            data-testid="task-type-search-input" 
            inputRef={inputRef}
            fullWidth
            size="small"
            disabled={disabled}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            placeholder={StringUtils.searchLabel}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon
                    sx={{
                      color: CommonColorUtils.noResultsTextColor,
                      fontSize: 18,
                    }}
                  />
                </InputAdornment>
              ),
            }}
            sx={styles.searchField}
          />
        </Box>

        <MenuItem
          data-testid="task-type-select-all" 
          onClick={disabled ? undefined : handleSelectAllToggle}
          sx={{
            ...styles.menuItem,
            backgroundColor:
              selectedValues.length === options.length
                ? CommonColorUtils.primaryGrey300
                : "white",
          }}
        >
          <Box sx={styles.iconBox}>
            {selectedValues.length === options.length ? (
              <CheckedIcon />
            ) : (
              <UnCheckedIcon />
            )}
          </Box>
          <ListItemText
            primary={
              <Typography sx={styles.optionText}>
                ({StringUtils.selectAll})
              </Typography>
            }
          />
        </MenuItem>

        {filteredOptions.length > 0 ? (
          filteredOptions.map((option) => {
            const selected = selectedValues.includes(option?.value);
            return (
              <MenuItem
                key={option.value}
                data-testid={`task-type-option-${option?.value}`} 
                disabled={disabled}
                onClick={disabled ? undefined : () => handleOptionToggle(option.value)}
                sx={{
                  ...styles.menuItem,
                  backgroundColor: selected
                    ? CommonColorUtils.primaryGrey300
                    : "white",
                }}
              >
                <Box sx={styles.iconBox}>
                  {selected ? <CheckedIcon /> : <UnCheckedIcon />}
                </Box>
                <ListItemText
                  primary={
                    <Typography
                      sx={{
                        fontSize: 14,
                        fontWeight: selected ? 600 : 500,
                      }}
                    >
                      {option.label}
                    </Typography>
                  }
                />
              </MenuItem>
            );
          })
        ) : (
          <MenuItem disabled sx={{ py: 0.5 }}>
            <Typography
              variant="body2"
              sx={styles.noResults}
              data-testid="task-type-no-results" 
            >
              {StringUtils.noResultFound}
            </Typography>
          </MenuItem>
        )}
      </Select>
    </FormControl>
  );
};

export default TaskTypeMultiSelectDropdown;
